<template>
  <div class="space-y-6">
    <section
      class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            System feature flags
          </h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Control platform-wide feature gates. These flags are evaluated
            before store-level settings, so disabling a flag here turns the
            feature off everywhere.
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            :disabled="loading"
            @click="fetchFlags"
          >
            <i
              :class="['fas mr-2', loading ? 'fa-sync fa-spin' : 'fa-sync']"
            ></i>
            Refresh
          </button>
          <button
            type="button"
            class="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            @click="openCreateForm()"
          >
            <i class="fas fa-plus mr-2"></i>
            New flag
          </button>
        </div>
      </div>

      <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div
          v-for="stat in summaryStats"
          :key="stat.label"
          class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40"
        >
          <p
            class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            {{ stat.label }}
          </p>
          <p class="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
            {{ stat.value }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ stat.caption }}
          </p>
        </div>
      </div>

      <div
        v-if="loadError"
        class="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
      >
        {{ loadError }}
      </div>
    </section>

    <section
      class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            Known controls
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Fast access to platform features that already have backend support.
          </p>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 lg:max-w-sm">
          QuickBooks still requires store-level connection and settings after the
          global flag is enabled.
        </p>
      </div>

      <div class="mt-4 grid gap-4 xl:grid-cols-2">
        <article
          v-for="flag in knownFlagRows"
          :key="flag.key"
          class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ flag.label }}
              </p>
              <p class="mt-1 text-xs uppercase tracking-wide text-gray-400">
                {{ flag.key }}
              </p>
            </div>
            <span
              class="inline-flex rounded-full px-3 py-1 text-xs font-semibold"
              :class="flag.enabled ? enabledBadgeClass : disabledBadgeClass"
            >
              {{ flag.enabled ? 'Enabled' : 'Disabled' }}
            </span>
          </div>
          <p class="mt-3 text-sm text-gray-600 dark:text-gray-300">
            {{ flag.helpText }}
          </p>
          <div class="mt-4 flex flex-wrap gap-2">
            <button
              v-if="flag.exists"
              type="button"
              class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
              :disabled="isBusy(flag.key)"
              @click="toggleFlag(flag)"
            >
              <i
                :class="[
                  'fas mr-2',
                  togglingKey === flag.key ? 'fa-sync fa-spin' : 'fa-power-off',
                ]"
              ></i>
              {{ flag.enabled ? 'Disable' : 'Enable' }}
            </button>
            <button
              type="button"
              class="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
              @click="flag.exists ? editFlag(flag) : openCreateForm(flag.key)"
            >
              <i
                :class="['fas mr-2', flag.exists ? 'fa-pen' : 'fa-plus']"
              ></i>
              {{ flag.exists ? 'Edit control' : 'Create control' }}
            </button>
          </div>
        </article>
      </div>
    </section>

    <div class="grid gap-6 xl:grid-cols-[minmax(0,1.6fr),minmax(320px,1fr)]">
      <section
        class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="relative flex-1 lg:max-w-md">
            <input
              v-model="search"
              type="search"
              placeholder="Search flags by key or description…"
              class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pl-9 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
            <span
              class="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"
            >
              <i class="fas fa-search"></i>
            </span>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Deleting a flag hides it and disables it until recreated.
          </p>
        </div>

        <div v-if="loading" class="mt-6 grid gap-3">
          <div
            v-for="index in 3"
            :key="index"
            class="h-28 animate-pulse rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900"
          ></div>
        </div>

        <div v-else-if="filteredFlags.length === 0" class="mt-6">
          <div
            class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center dark:border-gray-600 dark:bg-gray-900/30"
          >
            <div
              class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white shadow dark:bg-gray-800"
            >
              <i class="fas fa-sliders-h text-lg text-gray-400"></i>
            </div>
            <h3 class="mt-4 text-base font-semibold text-gray-700 dark:text-gray-100">
              No system flags found
            </h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Create a control like QUICKBOOKS_ONLINE_INTEGRATION to start
              managing platform-wide features from the dashboard.
            </p>
          </div>
        </div>

        <div v-else class="mt-6 space-y-3">
          <article
            v-for="flag in filteredFlags"
            :key="flag.key"
            class="rounded-lg border border-gray-200 p-4 transition hover:border-blue-300 dark:border-gray-700 dark:hover:border-blue-700"
          >
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {{ flag.key }}
                  </p>
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                    :class="flag.enabled ? enabledBadgeClass : disabledBadgeClass"
                  >
                    {{ flag.enabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {{ flag.description || 'No description provided.' }}
                </p>
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Updated {{ formatDate(flag.updated_at) }}
                  <span v-if="flag.metadata">• Metadata attached</span>
                </p>
                <pre
                  v-if="flag.metadata"
                  class="mt-3 overflow-x-auto rounded-md bg-gray-900/95 px-3 py-2 text-xs text-gray-100"
                >{{ formatMetadata(flag.metadata) }}</pre>
              </div>

              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
                  :disabled="isBusy(flag.key)"
                  @click="toggleFlag(flag)"
                >
                  <i
                    :class="[
                      'fas mr-2',
                      togglingKey === flag.key ? 'fa-sync fa-spin' : 'fa-power-off',
                    ]"
                  ></i>
                  {{ flag.enabled ? 'Disable' : 'Enable' }}
                </button>
                <button
                  type="button"
                  class="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
                  @click="editFlag(flag)"
                >
                  <i class="fas fa-pen mr-2"></i>
                  Edit
                </button>
                <button
                  type="button"
                  class="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200"
                  :disabled="isBusy(flag.key)"
                  @click="deleteFlag(flag)"
                >
                  <i
                    :class="[
                      'fas mr-2',
                      deletingKey === flag.key ? 'fa-sync fa-spin' : 'fa-trash',
                    ]"
                  ></i>
                  Delete
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section
        class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ formMode === 'edit' ? 'Edit feature flag' : 'Create feature flag' }}
            </h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {{
                formMode === 'edit'
                  ? 'Update the control status, description, or metadata.'
                  : 'Create a new system-level control and make it available to the platform.'
              }}
            </p>
          </div>
          <button
            type="button"
            class="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            @click="resetForm"
          >
            Reset form
          </button>
        </div>

        <datalist id="system-feature-flag-keys">
          <option v-for="flag in knownFlags" :key="flag.key" :value="flag.key" />
        </datalist>

        <form class="mt-6 space-y-4" @submit.prevent="saveFlag">
          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Flag key
            </label>
            <input
              v-model="form.key"
              type="text"
              list="system-feature-flag-keys"
              :disabled="formMode === 'edit'"
              placeholder="QUICKBOOKS_ONLINE_INTEGRATION"
              class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:disabled:bg-gray-800"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Keys are stored uppercase. Use delete + create if you need to rename one.
            </p>
          </div>

          <label
            class="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-900/40"
          >
            <input
              v-model="form.enabled"
              type="checkbox"
              class="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <span class="block font-semibold text-gray-900 dark:text-white">
                Enabled globally
              </span>
              <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                Disabling a flag here makes the guarded endpoints return unavailable even if store settings are configured.
              </span>
            </span>
          </label>

          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Description
            </label>
            <textarea
              v-model="form.description"
              rows="3"
              placeholder="Explain when this flag should be used."
              class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            ></textarea>
          </div>

          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Metadata JSON
            </label>
            <textarea
              v-model="form.metadataText"
              rows="8"
              placeholder='{"owner": "finance", "notes": "Roll out with caution"}'
              class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            ></textarea>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional. Leave blank to clear metadata.
            </p>
          </div>

          <div
            v-if="saveError"
            class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
          >
            {{ saveError }}
          </div>

          <div class="flex flex-wrap gap-2 pt-2">
            <button
              type="submit"
              class="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="saving"
            >
              <i
                :class="['fas mr-2', saving ? 'fa-sync fa-spin' : 'fa-save']"
              ></i>
              {{ formMode === 'edit' ? 'Save changes' : 'Create flag' }}
            </button>
            <button
              type="button"
              class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
              @click="resetForm"
            >
              Cancel
            </button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { toast } from "vue3-toastify";
import api from "@/helper/api";
import http from "@/helper/http";

const KNOWN_FLAGS = [
  {
    key: "SUBSCRIPTION_NOTIFICATIONS",
    label: "Subscription notifications",
    helpText: "Enables email and SMS confirmations and expiration reminders. Manage excluded stores/groups, message templates, and delivery history in the Messaging tab.",
  },
  {
    key: "QUICKBOOKS_ONLINE_INTEGRATION",
    label: "QuickBooks Online integration",
    helpText:
      "Enables the QuickBooks settings screens, OAuth flow, sync endpoints, and backend sync hooks for stores that are also configured individually.",
  },
];

const createDefaultForm = (key = "") => ({
  key,
  enabled: false,
  description: "",
  metadataText: "",
});

export default {
  name: "SystemFeatureFlagsPanel",
  setup() {
    const flags = ref([]);
    const loading = ref(false);
    const loadError = ref("");
    const search = ref("");

    const formMode = ref("create");
    const editingKey = ref("");
    const form = ref(createDefaultForm());
    const saveError = ref("");
    const saving = ref(false);
    const togglingKey = ref("");
    const deletingKey = ref("");

    const enabledBadgeClass =
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
    const disabledBadgeClass =
      "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200";

    const knownFlags = computed(() => KNOWN_FLAGS);

    const flagMap = computed(() => {
      return new Map(flags.value.map((flag) => [flag.key, flag]));
    });

    const knownFlagRows = computed(() => {
      return KNOWN_FLAGS.map((entry) => {
        const existing = flagMap.value.get(entry.key);
        return {
          ...entry,
          ...(existing || {}),
          exists: Boolean(existing),
          enabled: Boolean(existing?.enabled),
        };
      });
    });

    const filteredFlags = computed(() => {
      const query = search.value.trim().toLowerCase();
      if (!query) {
        return flags.value;
      }

      return flags.value.filter((flag) => {
        return (
          flag.key?.toLowerCase().includes(query) ||
          flag.description?.toLowerCase().includes(query)
        );
      });
    });

    const summaryStats = computed(() => {
      const total = flags.value.length;
      const enabledCount = flags.value.filter((flag) => flag.enabled).length;
      const disabledCount = total - enabledCount;
      const missingKnownFlags = KNOWN_FLAGS.filter(
        (entry) => !flagMap.value.has(entry.key)
      ).length;

      return [
        {
          label: "Total Controls",
          value: total,
          caption: "Flags currently stored in the system table",
        },
        {
          label: "Enabled",
          value: enabledCount,
          caption: "Flags that currently allow their guarded features",
        },
        {
          label: "Disabled",
          value: disabledCount,
          caption: "Flags that exist but are switched off",
        },
        {
          label: "Known Missing",
          value: missingKnownFlags,
          caption: "Suggested controls that have not been created yet",
        },
      ];
    });

    const isBusy = (key) => {
      return (
        saving.value || togglingKey.value === key || deletingKey.value === key
      );
    };

    const formatDate = (value) => {
      if (!value) {
        return "—";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatMetadata = (value) => {
      if (!value) {
        return "";
      }

      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return String(value);
      }
    };

    const fetchFlags = async () => {
      loading.value = true;
      loadError.value = "";
      try {
        const response = await http.get(api.admin_feature_flags);
        flags.value = Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to load system feature flags", error);
        loadError.value =
          error?.response?.data?.error?.message ||
          "Unable to load system feature flags.";
      } finally {
        loading.value = false;
      }
    };

    const openCreateForm = (key = "") => {
      formMode.value = "create";
      editingKey.value = "";
      saveError.value = "";

      const normalizedKey = String(key || "")
        .trim()
        .toUpperCase();
      const known = KNOWN_FLAGS.find((entry) => entry.key === normalizedKey);

      form.value = createDefaultForm(normalizedKey);
      if (known) {
        form.value.description = known.helpText;
      }
    };

    const editFlag = (flag) => {
      formMode.value = "edit";
      editingKey.value = flag.key;
      saveError.value = "";
      form.value = {
        key: flag.key,
        enabled: Boolean(flag.enabled),
        description: flag.description || "",
        metadataText: flag.metadata ? JSON.stringify(flag.metadata, null, 2) : "",
      };
    };

    const resetForm = () => {
      openCreateForm();
    };

    const buildPayload = () => {
      const normalizedKey = form.value.key.trim().toUpperCase();
      if (!normalizedKey) {
        throw new Error("A feature flag key is required.");
      }

      let metadata = null;
      const metadataText = form.value.metadataText.trim();
      if (metadataText) {
        try {
          metadata = JSON.parse(metadataText);
        } catch (error) {
          throw new Error("Metadata JSON must be valid JSON.");
        }
      }

      return {
        key: normalizedKey,
        body: {
          enabled: Boolean(form.value.enabled),
          description: form.value.description.trim() || null,
          metadata,
        },
      };
    };

    const saveFlag = async () => {
      saveError.value = "";
      saving.value = true;
      try {
        const payload = buildPayload();
        const response = await http.patch(
          api.admin_feature_flag(payload.key),
          payload.body
        );
        await fetchFlags();
        editFlag(response?.data || { ...payload.body, key: payload.key });
        toast.success(
          formMode.value === "edit"
            ? `Updated ${payload.key}.`
            : `Created ${payload.key}.`
        );
      } catch (error) {
        console.error("Failed to save system feature flag", error);
        saveError.value =
          error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to save the feature flag.";
      } finally {
        saving.value = false;
      }
    };

    const toggleFlag = async (flag) => {
      togglingKey.value = flag.key;
      try {
        await http.patch(api.admin_feature_flag(flag.key), {
          enabled: !flag.enabled,
          description: flag.description || null,
          metadata: flag.metadata || null,
        });
        await fetchFlags();
        if (editingKey.value === flag.key) {
          const refreshed = flagMap.value.get(flag.key);
          if (refreshed) {
            editFlag(refreshed);
          }
        }
        toast.success(
          `${flag.key} ${flag.enabled ? 'disabled' : 'enabled'}.`
        );
      } catch (error) {
        console.error("Failed to toggle feature flag", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to toggle the feature flag."
        );
      } finally {
        togglingKey.value = "";
      }
    };

    const deleteFlag = async (flag) => {
      if (
        !window.confirm(
          `Delete ${flag.key}? This disables the control until it is created again.`
        )
      ) {
        return;
      }

      deletingKey.value = flag.key;
      try {
        await http.delete(api.admin_feature_flag(flag.key));
        await fetchFlags();
        if (editingKey.value === flag.key) {
          resetForm();
        }
        toast.success(`Deleted ${flag.key}.`);
      } catch (error) {
        console.error("Failed to delete feature flag", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to delete the feature flag."
        );
      } finally {
        deletingKey.value = "";
      }
    };

    onMounted(() => {
      fetchFlags();
    });

    return {
      flags,
      loading,
      loadError,
      search,
      formMode,
      form,
      saveError,
      saving,
      togglingKey,
      deletingKey,
      enabledBadgeClass,
      disabledBadgeClass,
      knownFlags,
      knownFlagRows,
      filteredFlags,
      summaryStats,
      isBusy,
      formatDate,
      formatMetadata,
      fetchFlags,
      openCreateForm,
      editFlag,
      resetForm,
      saveFlag,
      toggleFlag,
      deleteFlag,
    };
  },
};
</script>